
import { useState } from 'react';
import { sanitizeInput, rateLimitCheck } from '@/utils/security';
import { useToast } from '@/components/ui/use-toast';

export const useForm = (initialState, validationRules = {}) => {
  const [formData, setFormData] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const { toast } = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = (data) => {
    const newErrors = {};
    
    Object.keys(validationRules).forEach(field => {
      const rule = validationRules[field];
      const value = data[field];
      
      if (rule.required && !value?.trim()) {
        newErrors[field] = rule.requiredMessage || `${field} is required`;
      } else if (rule.validator && !rule.validator(value)) {
        newErrors[field] = rule.message || `Invalid ${field}`;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (onSubmit, options = {}) => {
    if (isSubmitting) return false;

    // Rate limiting check
    if (!rateLimitCheck()) {
      toast({
        title: "Even geduld! ⏰",
        description: "Wacht 1 seconde voordat je opnieuw een bericht verstuurt.",
        variant: "destructive",
        duration: 3000,
      });
      return false;
    }

    // Sanitize all form data
    const sanitizedData = {};
    Object.keys(formData).forEach(key => {
      sanitizedData[key] = sanitizeInput(formData[key]);
    });

    // Validate form
    if (!validateForm(sanitizedData)) {
      return false;
    }

    setIsSubmitting(true);
    
    try {
      const result = await onSubmit(sanitizedData);
      
      if (result?.success !== false) {
        // Reset form on success
        if (options.resetOnSuccess !== false) {
          setFormData(initialState);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Form submission error:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData(initialState);
    setErrors({});
  };

  return {
    formData,
    isSubmitting,
    errors,
    handleChange,
    handleSubmit,
    resetForm,
    setFormData
  };
};
